import { useSyncExternalStore } from 'react';
import { KEYS, store } from './storage';

/**
 * Theme preference: follow the OS, or pin light/dark. index.html applies the
 * stored choice before first paint; this keeps it in sync afterwards.
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

const listeners = new Set<() => void>();
const media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function readPref(): ThemePref {
  const v = store.get(KEYS.theme);
  return v === 'light' || v === 'dark' ? v : 'system';
}

let pref: ThemePref = typeof window !== 'undefined' ? readPref() : 'system';

export function resolveTheme(p: ThemePref, systemDark: boolean): Theme {
  return p === 'system' ? (systemDark ? 'dark' : 'light') : p;
}

function apply() {
  const theme = resolveTheme(pref, media?.matches ?? false);
  document.documentElement.dataset.theme = theme;
  listeners.forEach((l) => l());
}

media?.addEventListener('change', () => {
  if (pref === 'system') apply();
});

export function setThemePref(next: ThemePref): void {
  pref = next;
  if (next === 'system') store.remove(KEYS.theme);
  else store.set(KEYS.theme, next);
  apply();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

const snapshot = () => `${pref}:${document.documentElement.dataset.theme ?? 'light'}`;

export function useTheme(): { pref: ThemePref; theme: Theme; setPref: (p: ThemePref) => void } {
  const snap = useSyncExternalStore(subscribe, snapshot, () => 'system:light');
  const [p, t] = snap.split(':') as [ThemePref, Theme];
  return { pref: p, theme: t, setPref: setThemePref };
}
